import React, { useCallback, useEffect, useState } from "react";
import { getPpsReport, savePpsReport, downloadPpsReportPdf } from "../api/client";
import { pushToast } from "../ui/toast";

/**
 * SCDES Pretreatment Performance Report (PPS): the questions of the SCDES
 * e-permitting form, in its order, for one half-year or calendar year. Values
 * PIMS works out are filled in (and can be overridden); facts kept on the POTW
 * Profile or a company are shown with where to change them; the rest are
 * answered here and saved per period. The PDF follows the same order, for
 * typing the answers into the portal.
 */

type When = { key: string; in: string[] };
type Field = {
  key: string; label: string; type: string; source: string; editable: boolean;
  value: any; calculated?: any; overridden?: boolean; where?: string; when?: When;
  options?: any[]; visible: boolean;
};
type Section = { key: string; title: string; subtitle?: string; company_id?: number; fields: Field[] };
type Report = {
  reporting_year: number; period: string; period_label: string; period_start: string; period_end: string;
  in_progress: boolean; sections: Section[]; missing: { section: string; label: string; where?: string }[];
  // Active companies with no permit in force in the period: not on the form, and why.
  left_out?: { company_id: number; name: string; reason: string }[];
};
// Unsaved edits: section key -> field key -> value.
type Edits = Record<string, Record<string, any>>;

const PERIODS = [["H1", "January – June"], ["H2", "July – December"], ["Y", "Calendar year"]];
const SOURCE_TAG: Record<string, string> = {
  calculated: "calculated", profile: "from POTW Profile", company: "from the company record", answer: "",
};

const isEmpty = (v: any) => v === null || v === undefined || v === "" || (Array.isArray(v) && v.length === 0);

function shown(field: Field, fields: Field[], value: (f: Field) => any): boolean {
  if (!field.when) return true;
  const other = fields.find(f => f.key === field.when!.key);
  return !!other && shown(other, fields, value) && field.when.in.includes(value(other));
}

function display(f: Field, v: any): string {
  if (isEmpty(v)) return "";
  if (f.type === "list") return (v as string[]).join("\n");
  if (f.type === "cfr") return (v as any[]).map(r => `${r.part}${r.subpart ? ` — ${r.subpart}` : ""}`).join("\n");
  return String(v);
}

export default function PpsReport() {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<number | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [edits, setEdits] = useState<Edits>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [open, setOpen] = useState<Record<string, boolean>>({ potw: true, interaction: true });

  const load = useCallback((y?: number, p?: string) => {
    setLoading(true); setError("");
    getPpsReport(y, p)
      .then(r => {
        setReport(r.data); setYear(r.data.reporting_year); setPeriod(r.data.period); setEdits({});
      })
      .catch(e => setError(e.response?.data?.error ?? "Couldn't load the report."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);   // opens on the last completed half-year

  const dirty = Object.values(edits).some(e => Object.keys(e).length > 0);
  const valueOf = (sec: Section) => (f: Field) =>
    edits[sec.key] && f.key in edits[sec.key] ? edits[sec.key][f.key] : f.value;
  const setValue = (sec: Section, key: string, v: any) =>
    setEdits(e => ({ ...e, [sec.key]: { ...(e[sec.key] ?? {}), [key]: v } }));

  const changePeriod = (y: number, p: string) => {
    if (dirty && !window.confirm("You have unsaved answers for this period. Leave without saving?")) return;
    load(y, p);
  };

  const save = async () => {
    if (!report || year === null || period === null) return;
    const answers: { potw: Record<string, any>; ius: Record<string, Record<string, any>> } = { potw: {}, ius: {} };
    for (const sec of report.sections) {
      const e = edits[sec.key];
      if (!e) continue;
      if (sec.company_id) answers.ius[String(sec.company_id)] = { ...e };
      else Object.assign(answers.potw, e);
    }
    setSaving(true); setError("");
    try {
      const r = await savePpsReport(year, period, answers);
      setReport(r.data); setEdits({});
      pushToast("Report answers saved.", "success");
    } catch (e: any) {
      setError(e.response?.data?.error ?? "Couldn't save the answers.");
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (year === null || period === null) return;
    if (dirty && !window.confirm("The PDF uses the saved answers. Download without your unsaved changes?")) return;
    try {
      const r = await downloadPpsReportPdf(year, period);
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Pretreatment Performance Report ${year} ${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Couldn't build the PDF.");
    }
  };

  const input = (sec: Section, f: Field) => {
    const v = valueOf(sec)(f);
    const label = f.label.length > 60 ? f.label.slice(0, 60) + "…" : f.label;
    if (f.type === "yesno" || f.type === "yesnona") {
      const opts = f.type === "yesno" ? ["Yes", "No"] : ["Yes", "No", "NA"];
      return (
        <select aria-label={label} value={v ?? ""} style={st.select}
          onChange={e => setValue(sec, f.key, e.target.value)}>
          {v === null && <option value="">—</option>}
          {opts.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    }
    if (f.type === "number") {
      return <input aria-label={label} type="number" min={0} step="any" style={st.num} value={v ?? ""}
        onChange={e => setValue(sec, f.key, e.target.value === "" ? null : e.target.value)} />;
    }
    if (f.type === "textarea") {
      return <textarea aria-label={label} style={st.textarea} value={v ?? ""} rows={2}
        onChange={e => setValue(sec, f.key, e.target.value)} />;
    }
    if (f.type === "counts") {
      const counts = (v ?? {}) as Record<string, number>;
      const total = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
      return (
        <table style={st.counts}><tbody>
          {(f.options ?? []).map((o: any) => (
            <tr key={o.key}>
              <td style={st.countLabel}>{o.label}</td>
              <td><input aria-label={o.label} type="number" min={0} step={1} style={st.num}
                value={counts[o.key] ?? 0}
                onChange={e => setValue(sec, f.key, { ...counts, [o.key]: e.target.value === "" ? 0 : Number(e.target.value) })} />
              </td>
            </tr>
          ))}
          <tr><td style={{ ...st.countLabel, textAlign: "right", fontWeight: 600 }}>Sum</td><td>{total}</td></tr>
        </tbody></table>
      );
    }
    return <input aria-label={label} style={st.text} value={v ?? ""} onChange={e => setValue(sec, f.key, e.target.value)} />;
  };

  const fieldRow = (sec: Section, f: Field) => {
    const v = valueOf(sec)(f);
    const edited = edits[sec.key] && f.key in edits[sec.key];
    const showCalc = f.source === "calculated" && f.editable &&
      ((f.overridden && !edited) || (edited && JSON.stringify(v) !== JSON.stringify(f.calculated ?? f.value)));
    const calc = f.calculated !== undefined ? f.calculated : f.value;
    return (
      <div key={f.key} style={st.field}>
        <div style={st.q}>{f.label}</div>
        <div style={st.a}>
          {f.editable ? input(sec, f) : (
            isEmpty(v)
              ? <span style={st.missing}>{f.source === "calculated" ? "None" :
                  `Not set — enter it in ${f.where ?? "the record"}`}</span>
              : <span style={{ whiteSpace: "pre-line" }}>{display(f, v)}</span>
          )}
          {SOURCE_TAG[f.source] && <span style={st.tag}>{SOURCE_TAG[f.source]}</span>}
          {showCalc && (
            <span style={st.calc}>
              changed from calculated {f.type === "counts" ? "counts" : `“${display(f, calc) || "blank"}”`} ·{" "}
              <button style={st.link} onClick={() => setValue(sec, f.key, calc)}>use calculated</button>
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={st.bar}>
        <label style={st.barLabel}>Year{" "}
          <select style={st.select} value={year ?? thisYear}
            onChange={e => changePeriod(Number(e.target.value), period ?? "H1")}>
            {Array.from({ length: 6 }, (_, i) => thisYear - i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label style={st.barLabel}>Period{" "}
          <select style={st.select} value={period ?? "H1"}
            onChange={e => changePeriod(year ?? thisYear, e.target.value)}>
            {PERIODS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button style={st.primary} onClick={save} disabled={!dirty || saving}>
            {saving ? "Saving…" : dirty ? "Save answers" : "Saved"}
          </button>
          <button style={st.secondary} onClick={downloadPdf} disabled={!report || loading}>Download PDF</button>
        </div>
      </div>

      {error && <div role="alert" style={st.error}>{error}</div>}
      {loading && !report && <p style={{ color: "#718096" }}>Assembling the report…</p>}

      {report && (
        <>
          <h3 style={{ margin: "4px 0 2px" }}>Pretreatment Performance Report — {report.period_label}</h3>
          <p style={st.lead}>
            The questions of the SCDES e-permitting form, in the same order. Calculated answers come from PIMS
            records and can be changed; download the PDF to copy them into the portal.
            {report.in_progress && <strong> This period has not ended yet — the figures will change.</strong>}
          </p>

          {(report.left_out ?? []).length > 0 && (
            <div role="status" style={{ ...st.todo, background: "#fff5f5", borderColor: "#feb2b2", color: "#9b2c2c" }}>
              <strong>{report.left_out!.length} industrial user(s) are not in this report</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {report.left_out!.map(lo => <li key={lo.company_id}>{lo.name}: {lo.reason}.</li>)}
              </ul>
            </div>
          )}

          {report.missing.length > 0 && (
            <div style={st.todo}>
              <strong>{report.missing.length} question(s) still need an answer</strong>
              <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                {report.missing.slice(0, 12).map((m, i) => (
                  <li key={i}>{m.section}: {m.label.length > 70 ? m.label.slice(0, 70) + "…" : m.label}
                    {m.where && <span style={{ color: "#975a16" }}> — enter it in {m.where}</span>}</li>
                ))}
                {report.missing.length > 12 && <li>…and {report.missing.length - 12} more (marked in the sections below)</li>}
              </ul>
            </div>
          )}

          {report.sections.map(sec => {
            const isOpen = open[sec.key] ?? false;
            const value = valueOf(sec);
            return (
              <div key={sec.key} style={st.section}>
                <button style={st.sectionHead} onClick={() => setOpen(o => ({ ...o, [sec.key]: !isOpen }))}
                  aria-expanded={isOpen}>
                  <span>{isOpen ? "▾" : "▸"} {sec.title}{sec.subtitle ? ` — ${sec.subtitle}` : ""}</span>
                  {edits[sec.key] && Object.keys(edits[sec.key]).length > 0 && <span style={st.unsaved}>unsaved</span>}
                </button>
                {isOpen && (
                  <div style={{ padding: "4px 14px 10px" }}>
                    {sec.fields.filter(f => shown(f, sec.fields, value)).map(f => fieldRow(sec, f))}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

const st: Record<string, React.CSSProperties> = {
  bar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 },
  barLabel: { fontSize: 13, fontWeight: 600, color: "#4a5568" },
  select: { padding: "5px 8px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13 },
  num: { padding: "5px 8px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13, width: 120 },
  text: { padding: "5px 8px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13, width: "100%", maxWidth: 520 },
  textarea: { padding: "5px 8px", border: "1px solid #cbd5e0", borderRadius: 5, fontSize: 13, width: "100%",
              maxWidth: 620, fontFamily: "inherit" },
  primary: { padding: "7px 18px", background: "#2b6cb0", color: "#fff", border: "none", borderRadius: 5,
             fontWeight: 600, cursor: "pointer" },
  secondary: { padding: "7px 18px", background: "#fff", color: "#2b6cb0", border: "2px solid #2b6cb0",
               borderRadius: 5, fontWeight: 600, cursor: "pointer" },
  error: { background: "#fff5f5", border: "1px solid #fc8181", color: "#c53030", borderRadius: 6,
           padding: "8px 12px", marginBottom: 12, fontSize: 13 },
  lead: { fontSize: 13, color: "#4a5568", margin: "0 0 12px" },
  todo: { background: "#fffaf0", border: "1px solid #fbd38d", borderRadius: 6, padding: "10px 14px",
          marginBottom: 14, fontSize: 13, color: "#7b341e" },
  section: { border: "1px solid #e2e8f0", borderRadius: 7, marginBottom: 10, background: "#fff" },
  sectionHead: { width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                 padding: "10px 14px", background: "#f7fafc", border: "none", borderRadius: 7, cursor: "pointer",
                 fontSize: 14, fontWeight: 700, color: "#1a365d", textAlign: "left" },
  unsaved: { fontSize: 11, fontWeight: 600, color: "#c05621" },
  field: { padding: "8px 0", borderBottom: "1px solid #edf2f7" },
  q: { fontSize: 13, fontWeight: 700, color: "#1a202c", marginBottom: 4 },
  a: { fontSize: 13, color: "#2d3748", display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" },
  tag: { fontSize: 11, color: "#718096", background: "#edf2f7", borderRadius: 10, padding: "1px 8px", alignSelf: "center" },
  calc: { fontSize: 11, color: "#975a16", alignSelf: "center" },
  link: { background: "none", border: "none", color: "#2b6cb0", cursor: "pointer", padding: 0, fontSize: 11,
          textDecoration: "underline" },
  missing: { color: "#c05621", fontStyle: "italic" },
  counts: { borderCollapse: "collapse", fontSize: 12 },
  countLabel: { padding: "3px 10px 3px 0", maxWidth: 420 },
};
